import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useEffect } from "react";
import { $insertNodes, $getSelection, $isRangeSelection } from "lexical";
import { convertMarkdownToLexicalNodes } from "@/features/notes/utils/convertMarkdownToLexicalNodes";

/**
 * Detects if text is likely markdown by checking for markdown-specific patterns
 * and excluding common cases that aren't markdown (like normal text with punctuation)
 */
function isLikelyMarkdown(text: string): boolean {
  // Count potential markdown indicators
  const indicators = {
    // Code blocks with triple backticks (strongest indicator - always treat as markdown)
    codeBlocks: (text.match(/```/g) || []).length,
    // Headings at line start
    headings: (text.match(/^#+\s/m) || []).length,
    // Numbered lists at line start
    numberedLists: (text.match(/^\d+\.\s/m) || []).length,
    // Unordered lists at line start
    unorderedLists: (text.match(/^[-*]\s/m) || []).length,
    // Checklist items at line start (strong indicator)
    checklists: (text.match(/^[-*]\s\[[^\]]*\]/m) || []).length,
    // Bold/italic with multiple markers (not just one or two)
    emphasis: (text.match(/[*_]{1,3}/g) || []).length,
    // Markdown tables with pipes and dashes
    tables: (text.match(/^\|?.+\|.+\n\|?[-:\s|]+\n/m) || []).length,
  };

  // Code blocks are the strongest indicator - if present, always treat as markdown
  // This ensures proper CodeNode creation on paste
  if (indicators.codeBlocks >= 1) return true;

  // Other strong indicators
  if (indicators.headings >= 1) return true;
  if (indicators.tables >= 1) return true;
  if (indicators.checklists >= 1) return true; // Checklist is a strong indicator
  if (indicators.numberedLists >= 2) return true;
  if (indicators.unorderedLists >= 2) return true;

  // If we have emphasis markers AND list/heading markers, it's likely markdown
  if (
    (indicators.numberedLists >= 1 || indicators.unorderedLists >= 1 || indicators.headings >= 1) &&
    indicators.emphasis >= 2
  ) {
    return true;
  }

  // Otherwise, treat as regular formatted content to preserve HTML styles
  return false;
}

export function MarkdownPastePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const text = event.clipboardData?.getData("text/plain");
      const html = event.clipboardData?.getData("text/html");

      if (!text && !html) {
        return;
      }

      // Check if this looks like markdown
      const isMarkdown = text ? isLikelyMarkdown(text) : false;

      // Check if this is a code block (strongest indicator)
      const hasCodeBlock = text ? /```/.test(text) : false;

      // Check if this is a table (before checking general markdown)
      const hasTable = text ? /^\|?.+\|.+\n\|?[-:\s|]+\n/m.test(text) : false;

      // For code blocks, tables and other markdown, convert using marked + @lexical/html
      if ((hasCodeBlock || hasTable || isMarkdown) && text) {
        event.preventDefault();
        event.stopPropagation();

        editor.update(
          () => {
            try {
              // Get current selection before we make changes
              const selection = $getSelection();

              // Delete selected text if there's a selection
              if (selection && $isRangeSelection(selection)) {
                selection.removeText();
              }

              // Convert markdown to Lexical nodes
              const nodes = convertMarkdownToLexicalNodes(editor, text);

              // Insert the converted nodes
              if (nodes && nodes.length > 0) {
                $insertNodes(nodes);
              }
            } catch (error) {
              console.error("Error converting markdown on paste:", error);
            }
          },
          { tag: "history" }
        );
        return; // Exit early to prevent any other paste handlers
      }
    };

    const editorElement = editor.getRootElement();
    if (editorElement) {
      editorElement.addEventListener("paste", handlePaste, true); // Use capture phase
      return () => {
        editorElement.removeEventListener("paste", handlePaste, true);
      };
    }
  }, [editor]);

  return null;
}
